import DeprecatedAutoTooltipContent from 'components/Common/DeprecatedAutoTooltipContent'
import { rem } from 'polished'
import type { RefObject } from 'react'
import { useRef } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { useIntl } from 'react-intl'
import SearchInputTrigger from '../SearchInputTrigger'
import Content from './Content'
import SearchTravellersDropdown from './SearchTravellersDropdown'
import zIndex from 'styles/tools/z-index'

const SearchTravellersInput = styled(SearchInputTrigger)`
  max-height: ${rem(50)};
`

/**
 * DropdownSheet uses position:fixed at zIndex.menu. Keep the guests trigger (and its
 * below-left error tooltip) in a higher sibling stacking context so the message isn't
 * covered by the open occupancy panel.
 */
const GuestsTriggerStack = styled.div<{ $elevate?: boolean }>`
  position: relative;
  width: 100%;

  ${props => props.$elevate && `
    z-index: ${zIndex.menu + 1};
  `}
`

const OccupancyErrorTooltip = styled(DeprecatedAutoTooltipContent)`
  && {
    z-index: ${zIndex.menu + 1};
  }
`

type ErrorTooltipPosition = 'above-left' | 'below-left'

interface Props {
  dropdownAnchorRef: RefObject<HTMLElement | null>
  dropdownPortaled?: boolean
  isToggled: boolean
  onTravellersToggle: (e: React.MouseEvent<HTMLButtonElement>) => void
  rooms: Array<App.Occupants>
  onRoomChange: (rooms: Array<App.Occupants>) => void
  onRoomsApply: () => void
  closeMenu: () => void
  isLastStep?: boolean
  error: boolean
  /** Defaults to above-left. Use below-left under sticky headers that would clip an above tooltip. */
  errorTooltipPosition?: ErrorTooltipPosition
  saleUnit?: string
  disabled?: boolean
  showAIGlow?: boolean
}

function SearchTravellersInputDesktop(props: Props) {
  const {
    dropdownAnchorRef,
    dropdownPortaled,
    rooms,
    onTravellersToggle,
    isToggled,
    onRoomChange,
    onRoomsApply,
    closeMenu,
    isLastStep,
    error,
    errorTooltipPosition = 'above-left',
    saleUnit,
    disabled,
    showAIGlow,
  } = props

  const intl = useIntl()
  const inputRef = useRef<HTMLButtonElement>(null)

  return (
    <>
      <GuestsTriggerStack $elevate={error}>
        <SearchTravellersInput
          ref={inputRef}
          onClick={onTravellersToggle}
          isToggled={isToggled}
          error={error}
          type="occupancy"
          disabled={!!disabled}
        >
          <Content showAIGlow={showAIGlow} />
          <OccupancyErrorTooltip
            open={error}
            position={errorTooltipPosition}
            alignment="left"
            type="error"
          >
            {intl.formatMessage({
              id: '-ST7cVfT7bzDFgPEO-BE',
              defaultMessage: 'Please select children’s ages',
              description: 'Error message displayed when children ages are not selected',
            })}
          </OccupancyErrorTooltip>
        </SearchTravellersInput>
      </GuestsTriggerStack>

      <SearchTravellersDropdown
        anchorRef={dropdownAnchorRef}
        portaled={dropdownPortaled}
        triggerRef={inputRef}
        rooms={rooms}
        open={isToggled}
        onRoomChange={onRoomChange}
        onRoomsApply={onRoomsApply}
        onClose={closeMenu}
        isLastStep={isLastStep}
        saleUnit={saleUnit}
      />
    </>
  )
}

export default SearchTravellersInputDesktop
